if (!customElements.get('show-more-button')) {
  customElements.define(
    'show-more-button',
    class ShowMoreButton extends HTMLElement {
      constructor() {
        super();
        const button = this.querySelector('button');
        button.addEventListener('click', (event) => {
          this.expandShowMore(event);
          const nextElementToFocus = event.target.closest('.parent-display').querySelector('.show-more-item');
          if (nextElementToFocus && !nextElementToFocus.classList.contains('hidden') && nextElementToFocus.querySelector('input')) {
            nextElementToFocus.querySelector('input').focus();
          }
        });
      }
      expandShowMore(event) {
        const parentDisplay = event.target.closest('[id^="Show-More-"]').closest('.parent-display');
        const parentWrap = parentDisplay.querySelector('.parent-wrap');
        this.querySelectorAll('.label-text').forEach((element) => element.classList.toggle('hidden'));
        parentDisplay.querySelectorAll('.show-more-item').forEach((item) => item.classList.toggle('hidden'));
        if (!this.querySelector('.label-show-less')) {
          this.classList.add('hidden');
        }
      }
    }
  );
}


/* ============================================================
   VF / Pebble & Wick — Cinematic Show More Enhancements
   Safe layer on top of Dawn show-more.js.
   Keeps filter expansion, focus movement and show less/show more labels intact.
   ============================================================ */

(() => {
  const ShowMoreButtonElement = customElements.get('show-more-button');
  if (!ShowMoreButtonElement || ShowMoreButtonElement.prototype.__vfCinematicPatched) return;

  ShowMoreButtonElement.prototype.__vfCinematicPatched = true;

  const originalExpandShowMore = ShowMoreButtonElement.prototype.expandShowMore;

  const root = document.documentElement;

  const getParentDisplay = (showMoreButton, event) =>
    event?.target?.closest?.('[id^="Show-More-"]')?.closest?.('.parent-display') ||
    showMoreButton.closest?.('.parent-display');

  const getVisibleItems = (parentDisplay) =>
    Array.from(parentDisplay?.querySelectorAll?.('.show-more-item') || []).filter(
      (item) => !item.classList.contains('hidden')
    );

  const setShowMoreState = (showMoreButton, state, detail = {}) => {
    showMoreButton.dataset.vfShowMoreState = state;

    showMoreButton.classList.toggle('vf-show-more-expanded', state === 'expanded');
    showMoreButton.classList.toggle('vf-show-more-collapsed', state === 'collapsed');
    showMoreButton.classList.toggle('vf-show-more-expanding', state === 'expanding');
    showMoreButton.classList.toggle('vf-show-more-hidden', state === 'hidden');

    root.classList.toggle('vf-show-more-is-expanding', state === 'expanding');

    showMoreButton.dispatchEvent(
      new CustomEvent(`vf:show-more-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const enhanceShowMoreButton = (showMoreButton) => {
    if (!showMoreButton || showMoreButton.__vfCinematicEnhanced) return;
    showMoreButton.__vfCinematicEnhanced = true;

    showMoreButton.classList.add('vf-show-more-button-enhanced');

    const button = showMoreButton.querySelector('button');
    const parentDisplay = showMoreButton.closest('.parent-display');

    button?.classList.add('vf-show-more-button__trigger');
    button?.setAttribute('aria-expanded', getVisibleItems(parentDisplay).length > 0 ? 'true' : 'false');

    parentDisplay?.classList.add('vf-show-more-parent');

    parentDisplay?.querySelectorAll('.show-more-item').forEach((item, index) => {
      item.classList.add('vf-show-more-item');
      item.style.setProperty('--vf-show-more-item-index', index);
    });

    setShowMoreState(showMoreButton, getVisibleItems(parentDisplay).length > 0 ? 'expanded' : 'collapsed', {
      initial: true,
      visibleCount: getVisibleItems(parentDisplay).length
    });
  };

  ShowMoreButtonElement.prototype.expandShowMore = function vfExpandShowMore(event) {
    enhanceShowMoreButton(this);

    const parentDisplay = getParentDisplay(this, event);
    const button = this.querySelector('button');
    const visibleBefore = getVisibleItems(parentDisplay).length;

    setShowMoreState(this, 'expanding', {
      event,
      visibleBefore,
      parentDisplay
    });

    let result;

    try {
      result = originalExpandShowMore.call(this, event);
    } catch (error) {
      this.classList.add('vf-show-more-error');

      this.dispatchEvent(
        new CustomEvent('vf:show-more-error', {
          bubbles: true,
          detail: {
            error,
            event
          }
        })
      );

      throw error;
    }

    const visibleAfter = getVisibleItems(parentDisplay).length;
    const isHidden = this.classList.contains('hidden');

    parentDisplay?.querySelectorAll('.show-more-item').forEach((item, index) => {
      const isVisible = !item.classList.contains('hidden');

      item.classList.toggle('vf-show-more-item-visible', isVisible);
      item.classList.toggle('vf-show-more-item-hidden', !isVisible);
      item.style.setProperty('--vf-show-more-visible-index', index);
    });

    button?.setAttribute('aria-expanded', visibleAfter > 0 ? 'true' : 'false');

    if (isHidden) {
      setShowMoreState(this, 'hidden', {
        event,
        visibleBefore,
        visibleAfter,
        parentDisplay
      });
    } else {
      setShowMoreState(this, visibleAfter > visibleBefore ? 'expanded' : 'collapsed', {
        event,
        visibleBefore,
        visibleAfter,
        parentDisplay
      });
    }

    this.classList.add('vf-show-more-activated');

    window.setTimeout(() => {
      this.classList.remove('vf-show-more-activated', 'vf-show-more-expanding');
      root.classList.remove('vf-show-more-is-expanding');
    }, 520);

    this.dispatchEvent(
      new CustomEvent('vf:show-more-updated', {
        bubbles: true,
        detail: {
          event,
          visibleBefore,
          visibleAfter,
          parentDisplay,
          hidden: isHidden
        }
      })
    );

    return result;
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('show-more-button').forEach(enhanceShowMoreButton);
  }, { once: true });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('show-more-button button');
    if (!trigger) return;

    trigger.classList.add('vf-show-more-clicked');

    window.setTimeout(() => {
      trigger.classList.remove('vf-show-more-clicked');
    }, 420);
  });

  const showMoreObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches?.('show-more-button')) {
          enhanceShowMoreButton(node);
        }

        node.querySelectorAll?.('show-more-button').forEach(enhanceShowMoreButton);
      });
    });
  });

  if (document.body) {
    showMoreObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
